import type { OfferView, RewardsResponse, UnsignedView, WalletResponse } from '@xchange/shared';
import { useEffect, useState } from 'react';
import { api, RequestError, type ActivityItem, type PairView } from '../api.js';
import { asksOf, bidsOf, finished, spreadOf, tradePrice, tradesOf } from '../book.js';
import type { LocalMoney } from '../money.js';
import { PHONE_LEVELS, PHONE_ROWS, PHONE_TRADES, useNarrow } from '../narrow.js';
import { bidReward, bidRewardText } from '../bidReward.js';
import { parseUsdPerGaju } from '@xchange/core';
import { amountText, assetName, bareText, inputText, parseAmount, type Asset } from '../assets.js';
import { netOfFee, priceBoxText } from '../prices.js';
import { normalizeDecimal } from '../numbers.js';
import type { Platform } from '../platform/index.js';
import { signUnsigned } from '../sign.js';
import { toast } from '../toast.js';
import { markBusy } from '../update.js';
import { BuyUsdcButton } from './BuyUsdc.js';
import { PinPad } from './PinPad.js';
import { GajuMark, UsdcMark, XgeMark } from './Symbols.js';
import { GAJU_FEE_RESERVE } from '../ticket.js';

/**
 * The Swap tab.
 *
 * The pairs first. Inside a pair: who is selling, who is buying, and two
 * buttons, Buy and Sell, each of which is an offer into escrow. Every step
 * that moves funds ends on the PIN pad, which names what is being signed.
 */
type Side = 'buy' | 'sell';
export type Signed = Awaited<ReturnType<typeof signUnsigned>>;
type Step =
  | { kind: 'pairs' }
  | { kind: 'pair'; pair: PairView }
  | { kind: 'make'; pair: PairView; side: Side }
  | {
      kind: 'pin';
      unsigned: UnsignedView;
      then: (signed: Signed, pin: string) => Promise<void>;
      back: Step;
      onCancel?: () => Promise<void>;
    };

export function Mark({ asset, size = 16 }: { asset: string; size?: number }) {
  if (asset === 'GAJU') return <GajuMark size={size} />;
  if (asset === 'USDC') return <UsdcMark size={size} />;
  return <XgeMark size={size} />;
}

export function ago(iso: string): string {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} d ago`;
}

/** "1,000 Gaju for $0.02" */
const DECIMALS: Record<Asset, bigint> = { GAJU: 18n, USDC: 6n, XGE: 6n };

/**
 * A price per single coin of the base, however the pair stores it: prices
 * for Gaju are kept per 1,000,000 because one Gaju is worth a fraction of a
 * cent, but what a person wants to read is "$0.0000124 per Gaju". Floored,
 * trailing zeros dropped, up to twelve decimals.
 */
export function perCoin(
  pair: Pick<PairView, 'base' | 'quote' | 'priceUnit'>,
  price: string | bigint,
): string {
  const base = pair.base as Asset;
  const quote = pair.quote as Asset;
  const num = BigInt(price) * 10n ** DECIMALS[base];
  const den = BigInt(pair.priceUnit) * 10n ** DECIMALS[quote];
  const whole = num / den;
  const frac = ((num % den) * 10n ** 12n) / den;
  const digits = frac.toString().padStart(12, '0').replace(/0+$/, '');
  const text = `${whole.toLocaleString('en-US')}${digits === '' ? '' : `.${digits}`}`;
  return quote === 'USDC' ? `$${text}` : `${text} ${assetName[quote]}`;
}

/**
 * The same price as a plain number of dollars, for reading out in the chosen
 * currency. Null on a pair that is not priced in USDC, where there is no
 * money figure to give: Gaju and XGE keep their own units.
 */
export function perCoinUsd(
  pair: Pick<PairView, 'base' | 'quote' | 'priceUnit'>,
  price: string | bigint,
): number | null {
  if (pair.quote !== 'USDC') return null;
  const base = pair.base as Asset;
  const num = Number(price) * 10 ** Number(DECIMALS[base]);
  const den = Number(pair.priceUnit) * 10 ** 6;
  return den === 0 ? null : num / den;
}

/** What is on offer now: the part still open, at the offer's price. */
export function offerText(pair: PairView, o: OfferView): string {
  // Nothing left on the market: the order's own size, said to be filled,
  // never "0.00 for $0.00" while its last part is still confirming.
  const filled = BigInt(o.remaining) === 0n;
  const base = filled ? BigInt(o.baseAmount) : BigInt(o.remaining);
  const quote = quoteFor(pair, base, BigInt(o.price));
  const text = `${amountText(pair.base as Asset, base)} for ${amountText(pair.quote as Asset, quote)}`;
  return filled ? `${text}, all filled` : text;
}

/** "$812.40" style volume, in the quote asset, as every amount is shown. */
export function volumeText(pair: PairView): string {
  return amountText(pair.quote as Asset, BigInt(pair.volume24h));
}

/** Percent change over 24 hours, one decimal, or null when there is nothing to compare. */
export function quoteFor(pair: Pick<PairView, 'priceUnit'>, base: bigint, price: bigint): bigint {
  return (base * price) / BigInt(pair.priceUnit);
}

/** Gaju held back for the network fee; defined with the ticket's arithmetic. */
export { GAJU_FEE_RESERVE };

/** Said on Gaju pairs while the Gajumaru network does not answer. */
export const GAJU_WAITS =
  'The Gajumaru network is not answering right now. Gaju orders wait; USDC and XGE orders go on as usual.';

export function Market({
  platform,
  pairs,
  open,
  mine,
  balances,
  solanaAddress = null,
  money,
  reload,
  rewards = null,
  activity = [],
  gajuWaits = false,
  preset = null,
}: {
  /**
   * A bid to open the form with, from Earn: the pair, Buy, its price and
   * amount typed in. `key` changes for each press, so the same bid twice opens twice.
   */
  preset?: { key: number; pair: string; price: bigint | null; amount: bigint | null } | null;
  platform: Platform;
  /** The chosen currency and the day's rates, held once for the whole app. */
  money: LocalMoney;
  /** No Gajumaru node answers: Gaju pairs say so in one line. Nothing is paused. */
  gajuWaits?: boolean;
  pairs: PairView[];
  open: OfferView[];
  mine: OfferView[];
  /** For the line on whether a bid earns the bid share; null hides it. */
  rewards?: RewardsResponse | null;
  /** The latest trades, which the bid share measures against with the book. */
  activity?: ActivityItem[];
  /** The wallet's balances, refreshed every few seconds; null until first read. */
  balances: WalletResponse['balances'] | null;
  /** The person's own Solana address, for the card route when USDC runs out. */
  solanaAddress?: string | null;
  reload: () => Promise<void>;
}) {
  const [step, setStep] = useState<Step>({ kind: 'pairs' });
  // The pair screen shows the best few on each side; the rest fold away.
  const [showAll, setShowAll] = useState<{ asks: boolean; bids: boolean }>({
    asks: false,
    bids: false,
  });
  // A phone shows less at once, the same way the browser app does: the last
  // trade alone, a few levels a side, three trades, three of your own orders.
  const narrow = useNarrow();
  const [statsOpen, setStatsOpen] = useState(false);
  const [yoursOpen, setYoursOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // A reload for a new release waits while something is in flight here.
  useEffect(() => {
    markBusy(busy);
    return () => {
      markBusy(false);
    };
  }, [busy]);
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  /** For Gaju pairs: the price typed per single Gaju or per million. */
  const [perGaju, setPerGaju] = useState(true);

  const fail = (e: unknown): void => {
    toast.err(
      e instanceof RequestError || e instanceof Error ? e.message : 'Something went wrong.',
    );
  };
  const sign = (
    unsigned: UnsignedView,
    back: Step,
    then: (s: Signed, pin: string) => Promise<void>,
    onCancel?: () => Promise<void>,
  ): void => {
    setStep({ kind: 'pin', unsigned, then, back, ...(onCancel === undefined ? {} : { onCancel }) });
  };

  /** The price in the pair's own unit, whichever way it was typed. */
  const priceUnits = (pair: PairView): bigint => {
    if (pair.base === 'GAJU' && perGaju) return parseUsdPerGaju(normalizeDecimal(price));
    return parseAmount(pair.quote as Asset, normalizeDecimal(price));
  };

  /**
   * Switch the price box between per Gaju and per 1,000,000, keeping the price
   * the same: the typed number is converted, never reread in the other unit.
   */
  const switchUnit = (pair: PairView, perOne: boolean): void => {
    if (perOne === perGaju) return;
    let units = 0n;
    try {
      units = priceUnits(pair);
    } catch {
      units = 0n;
    }
    setPerGaju(perOne);
    setPrice(units > 0n ? priceBoxText(pair, units, perOne) : '');
  };

  // From Earn: the form for a bid on the pair, with what Earn worked out typed in.
  useEffect(() => {
    if (preset === null) return;
    const pair = pairs.find((p) => p.name === preset.pair);
    if (pair === undefined) return;
    setPerGaju(true);
    setPrice(preset.price === null ? '' : priceBoxText(pair, preset.price, true));
    setAmount(preset.amount === null ? '' : inputText(pair.base as Asset, preset.amount));
    setStep({ kind: 'make', pair, side: 'buy' });
    // Only a new press opens it; the pairs reloading every few seconds do not.
  }, [preset?.key]);

  /** The pair as last loaded, so stats and the fee never go stale on an open screen. */
  const fresh = (pair: PairView): PairView => pairs.find((p) => p.name === pair.name) ?? pair;

  /**
   * Open the offer form with the market's best price already typed: a buyer
   * starts at the lowest seller, a seller at the highest buyer, so taking
   * the going rate is one tap and anything else is a number to change.
   */
  const startMake = (pair: PairView, side: Side): void => {
    const asks = asksOf(open, pair.name);
    const bids = bidsOf(open, pair.name);
    const best = side === 'buy' ? (asks[0] ?? bids[0]) : (bids[0] ?? asks[0]);
    // An empty book starts empty: a price left from another pair would mean
    // something else entirely here.
    setPrice(best === undefined ? '' : priceBoxText(pair, BigInt(best.price), perGaju));
    setAmount('');
    setStep({ kind: 'make', pair, side });
  };

  const make = async (pair: PairView, side: Side): Promise<void> => {
    setBusy(true);
    try {
      const baseAmount = parseAmount(pair.base as Asset, normalizeDecimal(amount));
      const p = priceUnits(pair);
      const { id, unsigned } = await api.createOffer({
        pair: pair.name,
        side,
        baseAmount: baseAmount.toString(),
        price: p.toString(),
      });
      // The first order paid on the other chain registers you there first,
      // so a match can pay you. One PIN signs both: the registration, then
      // the order itself, with nothing else to tap.
      if (unsigned.registerFirst === true) {
        sign(unsigned, { kind: 'make', pair, side }, async (signed, pin) => {
          await api.submitRegister(unsigned.chain, signed);
          const again = await api.createOffer({
            pair: pair.name,
            side,
            baseAmount: baseAmount.toString(),
            price: p.toString(),
          });
          if (again.unsigned.registerFirst === true)
            throw new Error('Still activating your wallet. Try again in a moment.');
          const signedOrder = await signUnsigned(platform, pin, again.unsigned);
          await api.submitOffer(again.id, signedOrder);
          toast.ok(
            side === 'buy'
              ? 'Bid placed. It fills against the best offers as soon as your deposit is seen.'
              : 'Offer placed. It fills against the best bids as soon as your deposit is seen.',
          );
          setAmount('');
          setPrice('');
          setStep({ kind: 'pair', pair });
          await reload();
        });
        return;
      }
      sign(unsigned, { kind: 'make', pair, side }, async (signed) => {
        await api.submitOffer(id, signed);
        toast.ok(
          side === 'buy'
            ? 'Bid placed. It fills against the best offers as soon as your deposit is seen.'
            : 'Offer placed. It fills against the best bids as soon as your deposit is seen.',
        );
        setAmount('');
        setPrice('');
        setStep({ kind: 'pair', pair });
        await reload();
      });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  /** A taker backing out before their deposit has gone; the server says if it is too late. */
  const undo = async (fillId: string): Promise<void> => {
    try {
      await api.untake(fillId);
      toast.ok('Undone. It is back on the market.');
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  /** One tap so the escrow can pay this wallet; the order fills right after. */
  const register = async (pair: PairView, chain: 'gajumaru' | 'solana'): Promise<void> => {
    try {
      const r = await api.registerEscrow(chain);
      if (r.registered || r.unsigned === undefined) {
        toast.ok('Already active. Your order can fill.');
        await reload();
        return;
      }
      sign(r.unsigned, { kind: 'pair', pair }, async (signed) => {
        await api.submitRegister(chain, signed);
        toast.ok('Activated. Your order fills as soon as the chain confirms it.');
        setStep({ kind: 'pair', pair });
        await reload();
      });
    } catch (e) {
      fail(e);
    }
  };

  const cancel = async (pair: PairView, offer: OfferView): Promise<void> => {
    try {
      const { unsigned } = await api.cancelOffer(offer.id);
      if (unsigned === null) {
        // Nothing was in escrow, so there was nothing to sign.
        toast.ok('Cancelled. Nothing was in escrow.');
        setStep({ kind: 'pair', pair });
        await reload();
        return;
      }
      sign(unsigned, { kind: 'pair', pair }, async (signed) => {
        await api.submitCancel(offer.id, signed);
        toast.ok('Cancelled. Your funds are on their way back.');
        setStep({ kind: 'pair', pair });
        await reload();
      });
    } catch (e) {
      fail(e);
    }
  };

  if (step.kind === 'pin') {
    return (
      <PinPad
        title="Your PIN"
        hint={`${step.unsigned.summary}. Signed on this phone.`}
        busy={busy}
        onPin={(pin) => {
          setBusy(true);
          void signUnsigned(platform, pin, step.unsigned)
            .then((signed) => step.then(signed, pin))
            .catch((e: unknown) => {
              fail(e);
              if (!(e instanceof Error && e.message === 'Wrong PIN')) {
                void step.onCancel?.();
                setStep(step.back);
              }
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      />
    );
  }

  // The whole row is the action: tap an offer to take it, or your own to
  // cancel it. A small coloured cue on the right says which.
  const row = (pair: PairView, o: OfferView, action?: { label: string; onClick: () => void }) => {
    const tone = action?.label === 'Buy' ? 'buy' : action?.label === 'Sell' ? 'sell' : 'quiet';
    const inner = (
      <>
        <div className="ob-text">
          <span className="ob-main">{offerText(pair, o)}</span>
          <span className="ob-sub">
            {perCoin(pair, o.price)} per {assetName[pair.base as Asset]}
            {o.house ? ' · xchange liquidity' : o.mine === null ? '' : ` · ${o.statusText}`}
          </span>
        </div>
        {action !== undefined && <span className={`ob-cue ${tone}`}>{action.label} ›</span>}
      </>
    );
    return action === undefined ? (
      <div className="ob-row" key={o.id}>
        {inner}
      </div>
    ) : (
      <button
        className="ob-row tap"
        key={o.id}
        disabled={busy}
        onClick={action.onClick}
        aria-label={`${action.label}: ${offerText(pair, o)}`}
        data-testid={`offer-${action.label.toLowerCase()}`}
      >
        {inner}
      </button>
    );
  };

  if (step.kind === 'make') {
    const { side } = step;
    const pair = fresh(step.pair);
    const base = pair.base as Asset;
    const quote = pair.quote as Asset;
    // What this order is paid with, and how much of it the wallet holds.
    const spend: Asset = side === 'sell' ? base : quote;
    const heldText = balances?.[spend] ?? null;
    const held = heldText === null ? null : BigInt(heldText);
    const reserve = spend === 'GAJU' ? GAJU_FEE_RESERVE : 0n;
    /** xchange's flat charge for a Solana order, paid with it in the same asset. */
    const orderFee = BigInt((side === 'sell' ? pair.orderFee.base : pair.orderFee.quote) ?? '0');
    /** What placing this order takes from the wallet, fee reserve and charge included; null until typed. */
    const needed = (): bigint | null => {
      try {
        const b = parseAmount(base, normalizeDecimal(amount));
        if (b <= 0n) return null;
        if (side === 'sell') return b + reserve + orderFee;
        const p = priceUnits(pair);
        return p > 0n ? quoteFor(pair, b, p) + reserve + orderFee : null;
      } catch {
        return null;
      }
    };
    const need = needed();
    const short = held !== null && need !== null && need > held;
    /** Fill in the most this wallet can place, exactly, at the typed price for a bid. */
    const useAll = (): void => {
      if (held === null) return;
      const usable = held > reserve ? held - reserve : 0n;
      if (usable === 0n) {
        toast.err(`No ${assetName[spend]} to use yet.`);
        return;
      }
      if (side === 'sell') {
        setAmount(inputText(base, usable));
        return;
      }
      let p = 0n;
      try {
        p = priceUnits(pair);
      } catch {
        p = 0n;
      }
      if (p <= 0n) {
        toast.err('Enter your price first.');
        return;
      }
      setAmount(inputText(base, (usable * BigInt(pair.priceUnit)) / p));
    };
    const est = (): string => {
      try {
        const b = parseAmount(base, normalizeDecimal(amount));
        const p = priceUnits(pair);
        return b > 0n && p > 0n ? amountText(quote, (b * p) / BigInt(pair.priceUnit)) : '';
      } catch {
        return '';
      }
    };
    return (
      <div className="tab-screen trade-screen">
        <div className="header">
          <button
            className="ghost"
            onClick={() => {
              setStep({ kind: 'pair', pair });
            }}
          >
            ‹ {pair.display}
          </button>
          <h2 className="title">
            {side === 'buy' ? 'Buy' : 'Sell'} {assetName[base]}
          </h2>
          <span style={{ width: 56 }} />
        </div>
        {gajuWaits && (pair.base === 'GAJU' || pair.quote === 'GAJU') && (
          <div className="card">
            <p className="small">{GAJU_WAITS}</p>
          </div>
        )}
        <label className="field">
          <div className="label label-row">
            <span>{side === 'sell' ? 'You sell' : 'You buy'}</span>
            {balances !== null && (
              <button
                type="button"
                className={`balance-chip${short ? ' short' : ''}`}
                onClick={useAll}
                disabled={held === null}
                aria-label={
                  held === null
                    ? `Reading your ${assetName[spend]} balance`
                    : `You have ${amountText(spend, held)}. Tap to use all of it.`
                }
                data-testid="offer-balance"
              >
                <span className="balance-chip-amount">
                  {held === null ? '…' : amountText(spend, held)}
                </span>
                {held !== null && <span className="balance-chip-max">Max</span>}
              </button>
            )}
          </div>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
            placeholder={`0 ${assetName[base]}`}
            data-testid="offer-amount"
          />
        </label>
        <label className="field">
          <div
            className="label"
            style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
          >
            <span>Your price, {assetName[quote]}</span>
            {base === 'GAJU' ? (
              <span className="seg" style={{ padding: 2 }}>
                <button
                  type="button"
                  aria-pressed={perGaju}
                  style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => {
                    switchUnit(pair, true);
                  }}
                >
                  per Gaju
                </button>
                <button
                  type="button"
                  aria-pressed={!perGaju}
                  style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => {
                    switchUnit(pair, false);
                  }}
                >
                  per 1,000,000
                </button>
              </span>
            ) : (
              <span>per {pair.priceUnitText}</span>
            )}
          </div>
          <input
            inputMode="decimal"
            value={price}
            onChange={(e) => {
              setPrice(e.target.value);
            }}
            placeholder={base === 'GAJU' && perGaju ? '$0.00002' : quote === 'USDC' ? '$0.00' : '0'}
            data-testid="offer-price"
          />
          {/* The price is typed, so the box stays in dollars; this reads it
              out in the chosen currency under it. */}
          {quote === 'USDC' &&
            (() => {
              let p = 0n;
              try {
                p = priceUnits(pair);
              } catch {
                return null;
              }
              if (p <= 0n) return null;
              const text = money.about(perCoinUsd(pair, p));
              return text === null ? null : (
                <span className="field-about" data-testid="tg-price-local">
                  {text} each
                </span>
              );
            })()}
        </label>
        {(() => {
          const asks = open.filter((o) => o.pair === pair.name && o.side === 'sell');
          const bids = open.filter((o) => o.pair === pair.name && o.side === 'buy');
          const show = (o: OfferView | undefined): string =>
            o === undefined
              ? 'none yet'
              : amountText(quote, BigInt(o.price)) + ' per ' + pair.priceUnitText;
          const lowest = asks.reduce<OfferView | undefined>(
            (m, o) => (m === undefined || BigInt(o.price) < BigInt(m.price) ? o : m),
            undefined,
          );
          const highest = bids.reduce<OfferView | undefined>(
            (m, o) => (m === undefined || BigInt(o.price) > BigInt(m.price) ? o : m),
            undefined,
          );
          return (
            <p className="small" style={{ marginTop: -6 }}>
              Best offer {show(lowest)} · best bid {show(highest)}. Change the price to whatever you
              want.
            </p>
          );
        })()}
        <div className="field">
          <div className="label">{side === 'sell' ? 'You get at least' : 'You pay at most'}</div>
          <div className="big" data-testid="offer-headline">
            {(() => {
              // A bid pays the whole amount into escrow; an offer's seller gets
              // it less the fee, so that is the figure promised.
              try {
                const q = quoteFor(
                  pair,
                  parseAmount(base, normalizeDecimal(amount)),
                  priceUnits(pair),
                );
                return amountText(quote, side === 'sell' ? netOfFee(pair, q) : q);
              } catch {
                return amountText(quote, 0n);
              }
            })()}
          </div>
          {/* The total is what gets signed, so it stays in USDC, with the
              chosen currency as an "about" beneath it. */}
          {quote === 'USDC' &&
            (() => {
              let net = 0n;
              try {
                net = quoteFor(pair, parseAmount(base, normalizeDecimal(amount)), priceUnits(pair));
              } catch {
                return null;
              }
              if (net <= 0n) return null;
              if (side === 'sell') net = netOfFee(pair, net);
              const text = money.about(Number(net) / 1e6);
              return text === null ? null : (
                <div className="field-about" data-testid="tg-total-local">
                  {text}
                </div>
              );
            })()}
          <div className="small" data-testid="net-after-fee">
            {(() => {
              let b = 0n;
              let q = 0n;
              try {
                b = parseAmount(base, normalizeDecimal(amount));
                q = quoteFor(pair, b, priceUnits(pair));
              } catch {
                return null;
              }
              if (b <= 0n || q <= 0n) return null;
              const pct = (pair.feeBps / 100).toString();
              const charge =
                orderFee > 0n
                  ? ` Plus ${amountText(spend, orderFee)} network, paid with the order.`
                  : '';
              return side === 'sell'
                ? `That is after the ${pct}% fee, at your price or better.${charge}`
                : `You receive ${amountText(base, netOfFee(pair, b))}, after the ${pct}% fee.${charge}`;
            })()}
          </div>
          <div className="small" data-testid="fills-now">
            {(() => {
              let want = 0n;
              let p = 0n;
              try {
                want = parseAmount(base, normalizeDecimal(amount));
                p = priceUnits(pair);
              } catch {
                return 'Enter an amount and a price.';
              }
              if (want <= 0n || p <= 0n) return 'Enter an amount and a price.';
              // What the best opposite offers cover now, best price first.
              const opposite = open
                .filter(
                  (o) =>
                    o.pair === pair.name &&
                    o.side !== side &&
                    o.mine !== 'maker' &&
                    (side === 'buy' ? BigInt(o.price) <= p : BigInt(o.price) >= p),
                )
                .sort((a, b) =>
                  side === 'buy'
                    ? BigInt(a.price) < BigInt(b.price)
                      ? -1
                      : 1
                    : BigInt(a.price) > BigInt(b.price)
                      ? -1
                      : 1,
                );
              let left = want;
              for (const o of opposite) {
                if (left === 0n) break;
                const r = BigInt(o.remaining);
                if (!o.partial && r > left) continue;
                left -= r < left ? r : left;
              }
              const now = want - left;
              if (now === 0n)
                return side === 'buy'
                  ? 'No offers at this price yet. Your bid waits in escrow until one comes, or until you cancel.'
                  : 'No bids at this price yet. Your offer waits in escrow until one comes, or until you cancel.';
              if (left === 0n)
                return `All of it fills right away against the best ${side === 'buy' ? 'offers' : 'bids'}.`;
              return `${amountText(base, now)} fills right away. The other ${amountText(base, left)} waits as your ${side === 'buy' ? 'bid' : 'offer'} until it fills, or you cancel.`;
            })()}
          </div>
          {(() => {
            if (side !== 'buy' || rewards === null) return null;
            let b = 0n;
            let p = 0n;
            try {
              b = parseAmount(base, normalizeDecimal(amount));
              p = priceUnits(pair);
            } catch {
              return null;
            }
            const r = bidReward({
              pair,
              base: b,
              price: p,
              open,
              trades: activity,
              rules: rewards,
              now: new Date(),
            });
            if (r === null) return null;
            return (
              <div className="small reward-hint" data-testid="bid-reward">
                <XgeMark size={14} />
                <span>{bidRewardText(r, rewards, (x) => perCoin(pair, x))}</span>
              </div>
            );
          })()}
        </div>
        {/* Without the USDC to pay with, refusing is half an answer: the way
            to get some is offered here. */}
        {side === 'buy' && short && spend === 'USDC' && (
          <div className="tg-no-usdc" data-testid="tg-no-usdc">
            <p className="small">
              You need USDC in this wallet to buy {assetName[base]}. You can buy some with a bank
              card, or send USDC you already have to your Solana address.
            </p>
            <BuyUsdcButton
              address={solanaAddress}
              from="tg-ticket"
              openLink={(url) => {
                platform.openLink(url);
              }}
              className="primary buy-usdc-cta"
              testId="tg-ticket-buy-usdc"
            />
          </div>
        )}
        <div className="spacer" />
        <button
          className="primary"
          disabled={busy || est() === '' || short}
          aria-busy={busy}
          onClick={() => void make(pair, side)}
          data-testid="offer-submit"
        >
          {short
            ? `Not enough ${assetName[spend]}`
            : side === 'sell'
              ? `Sell ${assetName[base]}`
              : `Buy ${assetName[base]}`}
        </button>
      </div>
    );
  }

  if (step.kind === 'pair') {
    const pair = fresh(step.pair);
    const base = pair.base as Asset;
    const asks = asksOf(open, pair.name);
    const bids = bidsOf(open, pair.name);
    const trades = tradesOf(activity, pair.name, narrow ? PHONE_TRADES : 10);
    const yours = mine.filter((o) => o.pair === pair.name && !finished(o.status));
    /** Your own orders and fills on this pair, as rows; a phone shows three. */
    const yourRows = yours.flatMap((o) => [
      ...(o.mine === 'maker'
        ? [
            row(
              pair,
              o,
              o.registerChain !== undefined
                ? {
                    label: 'Activate',
                    onClick: () => void register(pair, o.registerChain ?? 'solana'),
                  }
                : o.cancellable
                  ? {
                      label:
                        BigInt(o.remaining) === 0n && BigInt(o.leftover) > 0n
                          ? 'Take back'
                          : 'Cancel',
                      onClick: () => void cancel(pair, o),
                    }
                  : undefined,
            ),
          ]
        : []),
      ...o.fills
        .filter((f) => f.mine === 'taker' && f.status !== 'done' && f.status !== 'undone')
        .map((f) => (
          <div className="ob-row" key={f.id}>
            <div className="ob-text">
              <span className="ob-main">
                Taking {amountText(base, BigInt(f.baseAmount))} for{' '}
                {amountText(pair.quote as Asset, BigInt(f.quoteAmount))}
              </span>
              <span className="ob-sub">{f.statusText}</span>
            </div>
            {f.status === 'taking' && (
              <button
                className="ob-cue quiet"
                onClick={() => void undo(f.id)}
                data-testid="undo-take"
              >
                Undo ›
              </button>
            )}
          </div>
        )),
    ]);
    return (
      <div className="tab-screen trade-screen">
        <div className="header">
          <button
            className="ghost"
            onClick={() => {
              setStep({ kind: 'pairs' });
            }}
          >
            ‹ Pairs
          </button>
          <h2 className="title mark-row">
            <Mark asset={pair.logo} size={20} /> {pair.display}
          </h2>
          <span style={{ width: 56 }} />
        </div>
        {gajuWaits && (pair.base === 'GAJU' || pair.quote === 'GAJU') && (
          <div className="card">
            <p className="small">{GAJU_WAITS}</p>
          </div>
        )}
        <div className="card strip-card">
          <div className="strip">
            <div className="strip-col">
              <span className="strip-label">Sellers ask</span>
              <span className="strip-value" data-testid="tg-ask">
                {asks[0] === undefined
                  ? '—'
                  : money.read(perCoinUsd(pair, asks[0].price), perCoin(pair, asks[0].price))}
              </span>
            </div>
            <div className="strip-mid">
              <span className="strip-unit">per {assetName[pair.base as Asset]}</span>
              <span className="strip-open">
                {asks.length + bids.length === 0
                  ? 'nothing yet'
                  : `${asks.length + bids.length} open`}
              </span>
            </div>
            <div className="strip-col right">
              <span className="strip-label">Buyers bid</span>
              <span className="strip-value" data-testid="tg-bid">
                {bids[0] === undefined
                  ? '—'
                  : money.read(perCoinUsd(pair, bids[0].price), perCoin(pair, bids[0].price))}
              </span>
            </div>
          </div>
          <div
            className={`strip-stats${statsOpen || !narrow ? ' open' : ''}`}
            data-testid="pair-stats"
          >
            {pair.lastPrice === null ? (
              <span data-testid="tg-stat-last">No trades yet</span>
            ) : (
              <>
                <span data-testid="tg-stat-last">
                  Last {money.read(perCoinUsd(pair, pair.lastPrice), perCoin(pair, pair.lastPrice))}
                </span>
                <span className="strip-rest" data-testid="tg-stat-volume">
                  {pair.trades24h === 0 ? (
                    'No trades today'
                  ) : (
                    <>
                      24h volume {money.readAmount(pair.quote as Asset, BigInt(pair.volume24h))} ·{' '}
                      {pair.trades24h} {pair.trades24h === 1 ? 'trade' : 'trades'}
                    </>
                  )}
                </span>
                {narrow && (
                  <button
                    className="strip-more"
                    onClick={() => {
                      setStatsOpen(!statsOpen);
                    }}
                    aria-expanded={statsOpen}
                    data-testid="tg-stats-more"
                  >
                    {statsOpen ? 'Less' : 'More'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="actions">
          <button
            className="primary buy"
            disabled={!pair.live}
            onClick={() => {
              startMake(pair, 'buy');
            }}
            data-testid="pair-buy"
          >
            Buy {assetName[base]}
          </button>
          <button
            className="primary sell"
            disabled={!pair.live}
            onClick={() => {
              startMake(pair, 'sell');
            }}
            data-testid="pair-sell"
          >
            Sell {assetName[base]}
          </button>
        </div>
        {!pair.live && (
          <p className="small" style={{ textAlign: 'center' }}>
            This pair opens when its escrow is live on both chains.
          </p>
        )}
        {yours.length > 0 && (
          <div className="card ob-card" data-testid="my-offers">
            <div className="ob-head">
              <span className="label">Yours</span>
            </div>
            <div className="list">
              {narrow && !yoursOpen ? yourRows.slice(0, PHONE_ROWS) : yourRows}
            </div>
            {narrow && yourRows.length > PHONE_ROWS && (
              <button
                className="ghost ob-more"
                onClick={() => {
                  setYoursOpen(!yoursOpen);
                }}
                aria-expanded={yoursOpen}
                data-testid="tg-yours-more"
              >
                {yoursOpen ? 'Show fewer' : `Show all ${yourRows.length}`}
              </button>
            )}
          </div>
        )}
        <div className="card ob-card" data-testid="book">
          {(() => {
            const most = [...asks, ...bids].reduce((m, o) => {
              const r = BigInt(o.remaining);
              return r > m ? r : m;
            }, 1n);
            const rows = (key: 'asks' | 'bids', list: OfferView[]) =>
              (showAll[key] ? list : list.slice(0, PHONE_LEVELS)).map((o) => {
                const r = BigInt(o.remaining);
                const width = Number((r * 100n) / most);
                return (
                  <div className={`depth-row ${key}`} key={o.id}>
                    <span
                      className="depth-bar"
                      style={{ width: `${Math.max(width, 4)}%` }}
                      aria-hidden="true"
                    />
                    <span className="depth-price">{perCoin(pair, o.price)}</span>
                    <span className="depth-amount">
                      {bareText(base, r)}
                      {o.mine === 'maker' ? ' · yours' : ''}
                    </span>
                  </div>
                );
              });
            const more = (key: 'asks' | 'bids', list: OfferView[], noun: string) =>
              list.length > PHONE_LEVELS && (
                <button
                  className="ghost ob-more"
                  onClick={() => {
                    setShowAll((st) => ({ ...st, [key]: !st[key] }));
                  }}
                  data-testid={`${key}-more`}
                >
                  {showAll[key] ? `Show fewer ${noun}` : `Show all ${list.length} ${noun}`}
                </button>
              );
            const spread = spreadOf(asks, bids);
            return (
              <>
                <div className="depth-head">
                  <span>Price per {assetName[base]}</span>
                  <span>{assetName[base]}</span>
                </div>
                <div className="depth-side asks">
                  <span>Sellers</span>
                  <span>offers to sell, lowest price at the bottom</span>
                </div>
                {more('asks', asks, 'offers')}
                {asks.length === 0 ? (
                  <div className="depth-empty">No offers to sell {assetName[base]} yet.</div>
                ) : (
                  /* Lowest sell at the bottom, right above the best buy; the list grows upward. */
                  <div
                    className={showAll.asks ? 'depth-list up ob-all' : 'depth-list up'}
                    data-testid="asks"
                  >
                    {rows('asks', asks)}
                  </div>
                )}
                <div className="depth-spread" data-testid="spread">
                  {spread === null
                    ? '·'
                    : spread > 0n
                      ? `Spread ${perCoin(pair, spread)}`
                      : 'Matching'}
                </div>
                <div className="depth-side bids">
                  <span>Buyers</span>
                  <span>bids to buy, highest price at the top</span>
                </div>
                {bids.length === 0 ? (
                  <div className="depth-empty">No bids to buy {assetName[base]} yet.</div>
                ) : (
                  <div
                    className={showAll.bids ? 'depth-list ob-all' : 'depth-list'}
                    data-testid="bids"
                  >
                    {rows('bids', bids)}
                  </div>
                )}
                {more('bids', bids, 'bids')}
              </>
            );
          })()}
          <p className="small depth-note">
            Tap Buy to buy from the sellers at the lowest price, or Sell to sell to the buyers at
            the highest. It happens automatically. Set your own price if you want something else.
          </p>
        </div>
        {trades.length > 0 && (
          <div className="card ob-card" data-testid="tg-latest">
            <div className="ob-head">
              <span className="label">Latest trades</span>
            </div>
            {trades.map((t) => {
              const px = tradePrice(pair, t);
              return (
                <div className={`trade-row ${t.side}`} key={t.id}>
                  <span className="trade-amt">{bareText(base, BigInt(t.baseAmount))}</span>
                  <span className="trade-px">
                    {money.read(perCoinUsd(pair, px), perCoin(pair, px))}
                  </span>
                  <span className="trade-when">{ago(t.at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // The pairs.
  const active = mine.filter((o) => !finished(o.status));
  return (
    <div className="tab-screen trade-screen">
      <div className="header">
        <h2 className="title">Swap</h2>
      </div>
      <div className="card" style={{ padding: '6px var(--pad)' }}>
        <div className="list">
          {pairs.map((pair) => {
            const yours = active.filter((o) => o.pair === pair.name).length;
            return (
              <button
                className="row"
                key={pair.name}
                onClick={() => {
                  setStep({ kind: 'pair', pair });
                }}
                data-testid={`pair-${pair.name}`}
              >
                <div className="mark-row">
                  <Mark asset={pair.logo} size={26} />
                  <div>
                    <div className="desc" style={{ fontWeight: 500 }}>
                      {pair.display}
                    </div>
                    <div className="meta">
                      {!pair.live ? (
                        'Opening soon'
                      ) : pair.trades24h === 0 ? (
                        `No trades today${yours > 0 ? ` · ${yours} yours` : ''}`
                      ) : (
                        <>
                          24h volume {money.readAmount(pair.quote as Asset, BigInt(pair.volume24h))}{' '}
                          · {pair.trades24h} {pair.trades24h === 1 ? 'trade' : 'trades'}
                          {yours > 0 ? ` · ${yours} yours` : ''}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="amt" style={{ color: 'var(--hint)', fontWeight: 400 }}>
                  ›
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <p className="small" style={{ textAlign: 'center' }}>
        Pick a pair to buy or sell. Every trade settles through escrow on chain.
      </p>
    </div>
  );
}
